import type { SolutionDetail } from "@/types/detail";
import { SHARED_COMPARISON } from "@/data/detail.shared";
import {
  Boxes,
  Contact,
  Package,
  Warehouse,
  GraduationCap,
  Gauge,
  ShieldCheck,
  Zap,
  LineChart,
  Users,
  Clock,
} from "@/icons";

export const solutionsA: SolutionDetail[] = [
  {
    slug: "erp",
    title: "Enterprise Resource Planning",
    domain: "Operations",
    tagline: "One connected system for finance, inventory, sales and operations.",
    icon: Boxes,
    accent: "#0F766E",
    image: "/images/solutions/erp_solution.webp",
    overview:
      "A custom ERP unifies the moving parts of your business into a single, real-time platform — replacing disconnected tools and manual reconciliation with clarity and control.",
    whoShouldUse: [
      "Manufacturers and distributors managing complex operations.",
      "Multi-location businesses needing central oversight.",
      "Companies outgrowing accounting software and spreadsheets.",
    ],
    challenges: [
      "Departments working from conflicting versions of the truth.",
      "Manual reconciliation between finance, stock and sales.",
      "Expensive licences for modules you'll never use.",
      "No real-time picture of business health.",
    ],
    modules: [
      { title: "Finance & accounting", description: "Track cash, invoices and ledgers so leaders always know the numbers." },
      { title: "Inventory control", description: "Live stock levels prevent both shortages and costly overstock." },
      { title: "Procurement", description: "Smarter purchasing decisions from accurate demand data." },
      { title: "Sales & orders", description: "One connected flow from quote to fulfilment reduces delays." },
      { title: "Reporting & BI", description: "The KPIs leadership needs, on demand, without spreadsheets." },
      { title: "Role-based access", description: "Each team sees exactly what's relevant and permitted." },
    ],
    benefits: [
      { title: "One source of truth", description: "Every department works from the same live data.", icon: LineChart },
      { title: "Lower cost", description: "Only pay for the modules you actually need.", icon: Gauge },
      { title: "Fewer errors", description: "Automation replaces manual re-keying.", icon: ShieldCheck },
      { title: "Faster decisions", description: "Real-time visibility across the whole business.", icon: Zap },
    ],
    workflow: [
      { label: "Order", description: "A sales order is captured and validated against live stock and pricing." },
      { label: "Approve", description: "Approvals route automatically based on value and policy." },
      { label: "Fulfil", description: "Inventory is reserved and the fulfilment process kicks off." },
      { label: "Invoice", description: "Finance is updated automatically — no manual re-entry." },
      { label: "Report", description: "Dashboards reflect the transaction in real time for leadership." },
    ],
    integrations: ["Accounting", "CRM", "Payment gateways", "Shipping carriers", "Tax services", "BI tools"],
    typicalUsers: ["Operations managers", "Finance teams", "Warehouse staff", "Executives"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Why not just buy an off-the-shelf ERP?", answer: "Generic ERPs are heavy and rarely fit. Custom ERP includes only what you need and matches your real processes, avoiding costly workarounds." },
      { question: "How is it delivered?", answer: "In modules, so you get value early rather than waiting for one giant release." },
      { question: "Can you migrate our data?", answer: "Yes — migration and validation from your current systems is part of the project." },
      { question: "Does it integrate with accounting?", answer: "Yes, it integrates with finance, CRM and other systems you rely on." },
      { question: "Is it secure?", answer: "Role-based access, audit trails and encryption are standard." },
      { question: "Can it handle multiple locations?", answer: "Yes, multi-site and multi-entity operations are supported." },
    ],
    relatedServices: ["erp-development", "workflow-automation", "web-applications"],
    relatedIndustries: ["logistics", "ecommerce", "healthcare"],
    caseConcept: {
      title: "Cascade — modular ERP",
      category: "ERP",
      summary: "A concept ERP unifying finance, inventory and orders for a multi-site distributor, cutting manual work and month-end close time.",
      metrics: [{ label: "Sites", value: "5" }, { label: "Manual work", value: "-60%" }, { label: "Close time", value: "-40%" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#0F766E",
    },
  },
  {
    slug: "crm",
    title: "Customer Relationship Management",
    domain: "Sales & Service",
    tagline: "A CRM built around your pipeline, with automation that grows revenue.",
    icon: Contact,
    accent: "#DB2777",
    image: "/images/solutions/crm_solution.webp",
    overview:
      "A custom CRM matches how your team actually sells and supports customers, capturing every interaction and automating the busywork so relationships — and revenue — grow.",
    whoShouldUse: [
      "Sales teams frustrated by rigid, generic CRMs.",
      "Service businesses managing long customer relationships.",
      "Companies wanting automation tailored to their pipeline.",
    ],
    challenges: [
      "Customer data scattered across inboxes and spreadsheets.",
      "Low CRM adoption because the tool doesn't fit.",
      "No reliable view of pipeline or forecast.",
      "Follow-ups slipping through the cracks.",
    ],
    modules: [
      { title: "Contact & account 360", description: "Every interaction in one place so nothing is forgotten." },
      { title: "Custom pipelines", description: "Stages mirror how your team really sells, boosting adoption." },
      { title: "Activity automation", description: "Auto-logged emails and tasks free reps to sell." },
      { title: "Lead scoring", description: "Focus effort where it's most likely to convert." },
      { title: "Forecasting", description: "Reliable numbers leaders can plan around." },
      { title: "Service & tickets", description: "Support history that makes every conversation informed." },
    ],
    benefits: [
      { title: "Higher win rates", description: "Timely nudges and clear pipelines close more deals.", icon: LineChart },
      { title: "Less admin", description: "Automation handles logging and reminders.", icon: Zap },
      { title: "Trustworthy forecasts", description: "Decisions based on real pipeline data.", icon: Gauge },
      { title: "Happier customers", description: "Full history makes every touchpoint personal.", icon: Users },
    ],
    workflow: [
      { label: "Capture", description: "Leads flow in from forms, email and imports, de-duplicated automatically." },
      { label: "Qualify", description: "Scoring highlights the opportunities worth pursuing first." },
      { label: "Nurture", description: "Automated follow-ups keep deals moving without manual effort." },
      { label: "Close", description: "Guided stages and reminders drive deals to a decision." },
      { label: "Retain", description: "Service history and renewals keep customers for the long term." },
    ],
    integrations: ["Email & calendar", "Marketing tools", "Telephony", "Payment", "ERP", "Analytics"],
    typicalUsers: ["Sales reps", "Sales leaders", "Support agents", "Marketing"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "How is this better than a popular CRM?", answer: "It fits your exact process so your team actually uses it, and automates steps generic CRMs can't." },
      { question: "Can you migrate our data?", answer: "Yes, we migrate and de-duplicate contacts and history from your current tools." },
      { question: "Does it sync with email?", answer: "Yes — activity captures automatically to reduce manual logging." },
      { question: "Can we automate our workflow?", answer: "Automated tasks, reminders and routing are core features." },
      { question: "Is reporting customisable?", answer: "Dashboards are built around the metrics you care about." },
      { question: "Will it scale with our team?", answer: "Yes, it grows with more users, pipelines and data." },
    ],
    relatedServices: ["crm-development", "ai-integration", "workflow-automation"],
    relatedIndustries: ["saas", "fintech", "ecommerce"],
    caseConcept: {
      title: "Vertex — sales CRM",
      category: "CRM",
      summary: "A concept CRM with AI-assisted forecasting and automated follow-ups tailored to a B2B multi-stage pipeline.",
      metrics: [{ label: "Win rate", value: "+22%" }, { label: "Admin time", value: "-45%" }, { label: "Forecast", value: "+30%" }],
      tech: ["React", "FastAPI", "OpenAI"],
      accent: "#DB2777",
    },
  },
  {
    slug: "asset-management",
    title: "Asset Management System",
    domain: "Operations",
    tagline: "Track every asset across its lifecycle — from purchase to retirement.",
    icon: Package,
    accent: "#0891B2",
    image: "/images/solutions/asset_management_solution.webp",
    overview:
      "Know what you own, where it is and what condition it's in. An asset management system replaces guesswork and spreadsheets with a live register, maintenance schedules and clear accountability.",
    whoShouldUse: [
      "Organisations with valuable physical or IT assets.",
      "Facilities and operations teams tracking equipment.",
      "Businesses needing audit-ready asset records.",
    ],
    challenges: [
      "No reliable record of what's owned or where it is.",
      "Missed maintenance leading to costly failures.",
      "Assets lost, unused or duplicated unnecessarily.",
      "Painful, manual audits and depreciation tracking.",
    ],
    modules: [
      { title: "Asset register", description: "A single live record of every asset and its details." },
      { title: "Assignment & tracking", description: "Always know who holds what and where it lives." },
      { title: "Maintenance scheduling", description: "Prevent failures with planned, tracked servicing." },
      { title: "Depreciation & finance", description: "Accurate values for finance and audits, automatically." },
      { title: "QR / barcode scanning", description: "Fast check-in and check-out from any device." },
      { title: "Alerts & reporting", description: "Warranty, service and audit reminders that reduce risk." },
    ],
    benefits: [
      { title: "Reduce losses", description: "Full visibility stops assets going missing or unused.", icon: ShieldCheck },
      { title: "Fewer breakdowns", description: "Scheduled maintenance extends asset life.", icon: Clock },
      { title: "Audit-ready", description: "Accurate records make audits fast and painless.", icon: Gauge },
      { title: "Better decisions", description: "Know when to repair, replace or reallocate.", icon: LineChart },
    ],
    workflow: [
      { label: "Register", description: "Each asset is added with details, value and location." },
      { label: "Assign", description: "Assets are checked out to people, teams or sites." },
      { label: "Maintain", description: "Scheduled servicing is tracked and reminders sent." },
      { label: "Audit", description: "Scan-based checks reconcile records quickly and accurately." },
      { label: "Retire", description: "End-of-life is recorded with proper depreciation and disposal." },
    ],
    integrations: ["ERP", "Accounting", "Procurement", "Barcode scanners", "SSO", "BI tools"],
    typicalUsers: ["Facilities managers", "IT teams", "Finance", "Field technicians"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Can we track both IT and physical assets?", answer: "Yes — any asset type can be tracked, from laptops to machinery and vehicles." },
      { question: "Does it support barcodes or QR codes?", answer: "Yes, scan-based check-in/out and audits work from any device." },
      { question: "Can it handle depreciation?", answer: "Yes, values and depreciation are calculated for finance and audits." },
      { question: "Will it integrate with our ERP?", answer: "It integrates with ERP, accounting and procurement systems." },
      { question: "Can we schedule maintenance?", answer: "Yes, with automated reminders to prevent missed servicing." },
      { question: "Is it accessible in the field?", answer: "Yes, it's responsive and works on mobile devices on-site." },
    ],
    relatedServices: ["web-applications", "workflow-automation", "mobile-app-development"],
    relatedIndustries: ["logistics", "healthcare", "education"],
    caseConcept: {
      title: "Meridian — asset register",
      category: "Asset Management",
      summary: "A concept system giving a facilities team a live asset register with QR check-in and scheduled maintenance, cutting losses and downtime.",
      metrics: [{ label: "Losses", value: "-35%" }, { label: "Downtime", value: "-25%" }, { label: "Audit time", value: "-70%" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#0891B2",
    },
  },
  {
    slug: "inventory-management",
    title: "Inventory Management",
    domain: "Operations",
    tagline: "Real-time stock control that prevents shortages and overstock.",
    icon: Warehouse,
    image: "/images/solutions/asset_management_solution.webp",
    accent: "#0F766E",
    overview:
      "See true stock levels across locations, automate reordering and fulfil faster. An inventory system turns guesswork into confident, data-driven stock decisions.",
    whoShouldUse: [
      "Retailers and wholesalers managing stock.",
      "Businesses selling across multiple channels.",
      "Warehouses needing accurate, live counts.",
    ],
    challenges: [
      "Stockouts losing sales and overstock tying up cash.",
      "Manual counts that are outdated the moment they're done.",
      "Inventory scattered across channels and locations.",
      "No visibility into what's selling or slow-moving.",
    ],
    modules: [
      { title: "Live stock levels", description: "Accurate counts across every location and channel." },
      { title: "Automated reordering", description: "Reorder points prevent stockouts without overbuying." },
      { title: "Barcode & SKU", description: "Fast, error-free receiving, picking and counts." },
      { title: "Multi-location", description: "Move and balance stock across warehouses easily." },
      { title: "Order fulfilment", description: "Faster, more accurate picking and dispatch." },
      { title: "Analytics", description: "Spot best-sellers and dead stock to free up cash." },
    ],
    benefits: [
      { title: "Fewer stockouts", description: "Never miss a sale to empty shelves again.", icon: LineChart },
      { title: "Less tied-up cash", description: "Avoid overstock by ordering to real demand.", icon: Gauge },
      { title: "Faster fulfilment", description: "Accurate stock speeds up picking and dispatch.", icon: Zap },
      { title: "Accurate counts", description: "Live data replaces error-prone manual counts.", icon: ShieldCheck },
    ],
    workflow: [
      { label: "Receive", description: "Incoming stock is scanned and added to live counts." },
      { label: "Store", description: "Items are allocated to locations for efficient picking." },
      { label: "Sell", description: "Sales across channels decrement stock in real time." },
      { label: "Reorder", description: "Reorder points trigger purchasing before you run out." },
      { label: "Analyse", description: "Reports reveal what to stock more — and less — of." },
    ],
    integrations: ["E-commerce", "POS", "ERP", "Shipping", "Accounting", "Barcode scanners"],
    typicalUsers: ["Warehouse teams", "Purchasing", "Store managers", "Finance"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Can it handle multiple locations?", answer: "Yes, stock is tracked and balanced across all warehouses and stores." },
      { question: "Does it sync with our online store?", answer: "Yes, it integrates with e-commerce and POS to keep stock accurate everywhere." },
      { question: "Can reordering be automated?", answer: "Yes, reorder points trigger purchasing before you run out." },
      { question: "Does it support barcodes?", answer: "Yes, barcode and SKU scanning speed up every stock task." },
      { question: "Will it integrate with accounting?", answer: "Yes, inventory values flow through to your finance system." },
      { question: "Can we see sales trends?", answer: "Analytics highlight best-sellers and slow-moving stock." },
    ],
    relatedServices: ["erp-development", "web-applications", "workflow-automation"],
    relatedIndustries: ["ecommerce", "logistics", "fintech"],
    caseConcept: {
      title: "Vertex — inventory hub",
      category: "Inventory Management",
      summary: "A concept system syncing stock across store, warehouse and online channels, eliminating stockouts and reducing excess inventory.",
      metrics: [{ label: "Stockouts", value: "-80%" }, { label: "Excess stock", value: "-30%" }, { label: "Fulfil time", value: "-40%" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#0F766E",
    },
  },
  {
    slug: "school-erp",
    title: "School ERP",
    domain: "Education",
    tagline: "Run admissions, academics and administration on one platform.",
    icon: GraduationCap,
    accent: "#4F46E5",
    image: "/images/solutions/school_erp.webp",
    overview:
      "A school ERP connects admissions, attendance, grades, fees and communication in one place — freeing staff from paperwork and giving parents and students clarity.",
    whoShouldUse: [
      "Schools and colleges managing many students.",
      "Institutions juggling disconnected admin tools.",
      "Education groups running multiple campuses.",
    ],
    challenges: [
      "Admin buried in paperwork and spreadsheets.",
      "Fee collection and tracking that's manual and messy.",
      "Poor communication between school, parents and students.",
      "No single view of a student's progress.",
    ],
    modules: [
      { title: "Admissions", description: "Streamline enquiries to enrolment without paperwork." },
      { title: "Attendance", description: "Fast, accurate attendance with automatic alerts." },
      { title: "Gradebook", description: "Record and share results transparently with families." },
      { title: "Fee management", description: "Automated invoicing, reminders and reconciliation." },
      { title: "Parent & student portal", description: "Clear access to schedules, results and notices." },
      { title: "Timetabling", description: "Balanced schedules for classes, staff and rooms." },
    ],
    benefits: [
      { title: "Less admin", description: "Automation frees staff to focus on students.", icon: Clock },
      { title: "On-time fees", description: "Automated reminders improve collection rates.", icon: LineChart },
      { title: "Engaged parents", description: "Portals keep families informed and involved.", icon: Users },
      { title: "Better oversight", description: "One view of every student's progress.", icon: Gauge },
    ],
    workflow: [
      { label: "Enrol", description: "Applicants move from enquiry to enrolment in a guided flow." },
      { label: "Schedule", description: "Timetables are generated for classes, staff and rooms." },
      { label: "Teach", description: "Attendance and grades are recorded through the term." },
      { label: "Bill", description: "Fees are invoiced with automatic reminders and receipts." },
      { label: "Report", description: "Progress and finance reports reach parents and leadership." },
    ],
    integrations: ["Payment gateways", "Email & SMS", "LMS", "Accounting", "SSO", "Biometric attendance"],
    typicalUsers: ["Administrators", "Teachers", "Parents", "Students"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Can it manage multiple campuses?", answer: "Yes, multi-campus institutions can run centrally with per-site views." },
      { question: "Does it handle fee collection?", answer: "Yes, with automated invoicing, reminders and payment integration." },
      { question: "Is there a parent portal?", answer: "Yes, parents get clear access to schedules, results and notices." },
      { question: "Can teachers record grades and attendance?", answer: "Yes, quickly from any device, with alerts to families." },
      { question: "Does it integrate with an LMS?", answer: "Yes, it can connect to learning platforms and communication tools." },
      { question: "Is student data kept secure?", answer: "Yes, with role-based access and strong data protection." },
    ],
    relatedServices: ["erp-development", "web-applications", "mobile-app-development"],
    relatedIndustries: ["education", "healthcare", "saas"],
    caseConcept: {
      title: "Lumen Academy — school platform",
      category: "School ERP",
      summary: "A concept school ERP unifying admissions, attendance, grades and fees, cutting admin time and improving fee collection.",
      metrics: [{ label: "Admin time", value: "-50%" }, { label: "Fee on-time", value: "+35%" }, { label: "Campuses", value: "3" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#4F46E5",
    },
  },
];
